import React, { useEffect, useState, useRef, Suspense, lazy, createElement, Component } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, StarIcon, PlayIcon, PauseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { AudioPlayer } from '../components/AudioPlayer';
// Lazy loaded components
const TestimonialsInt02 = lazy(() => import('../components/TestimonialsInt02').then(m => ({
  default: m.TestimonialsInt02
})));
const FAQInt02 = lazy(() => import('../components/FAQInt02').then(m => ({
  default: m.FAQInt02
})));
const SocialProofNotificationInt02 = lazy(() => import('../components/SocialProofNotificationInt02').then(m => ({
  default: m.SocialProofNotificationInt02
})));
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
const trackInt02 = (_name: string, _price: number) => {
  // AddToCart/InitiateCheckout removed
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const BG = '#0B0B0B';
// ─── Pricing ──────────────────────────────────────────────────────────────────
const BASE_PRICE = 59.97;
const TRIVIA_PRICE = 9.97;
const COMPANION_PRICE = 17.97;
const HILLVALLEY_PRICE = 12.97;
const AUDIOBOOK_PRICE = 24.97;
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://library.legendaryreads.com/cf/click/1';
const CHECKOUT_BUNDLE = 'https://library.legendaryreads.com/cf/click/2';
// ─── Shared primitives ────────────────────────────────────────────────────────
const MobileAudioButtonInt02: React.FC<{
  src: string;
}> = ({
  src
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleEnded = () => setIsPlaying(false);
  return <>
      <audio ref={audioRef} src={src} preload="none" onEnded={handleEnded} data-id="element-698" />
      <button type="button" onClick={toggle} className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-600 to-orange-500 hover:from-orange-500 hover:to-yellow-500 flex items-center justify-center transition-all duration-300 shadow-lg ${isPlaying ? 'shadow-orange-500/60 scale-95' : 'shadow-orange-600/30'}`} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-699">
        {isPlaying ? <PauseIcon className="h-5 w-5 text-white" data-id="element-700" /> : <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-701" />}
      </button>
    </>;
};
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  children: React.ReactNode;
  small?: boolean;
}) => {
  const style = {
    minHeight: small ? '44px' : '56px',
    padding: small ? '10px 16px' : '16px 24px',
    fontSize: small ? '14px' : '17px',
    background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
    color: '#0B0B0B',
    fontFamily: 'Inter, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const className = 'cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg cursor-pointer';
  if (href) {
    return <a href={href} onClick={onClick as any} className={className} style={style} data-id="element-702">
        {children}
      </a>;
  }
  return <button onClick={onClick as any} className={className} style={style} data-id="element-703">
      {children}
    </button>;
};
// ─── HERO SECTION ─────────────────────────────────────────────────────────
const HeroInt02 = () => <section className="py-4 md:py-6 relative overflow-hidden" style={{
  backgroundColor: BG
}} data-id="element-704">
    <div className="absolute inset-0 opacity-20" style={{
    background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
  }} data-id="element-705" />
    <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10" data-id="element-706">
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-707">
        {/* Left: copy */}
        <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-708">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-709">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-710">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-711">
              You've Never Read Anything Like This.
            </span>
          </h1>
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-712">
            The only collector's edition that goes beyond the film, into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Digital. Yours to
            keep permanently.
          </p>

          {/* Mobile image */}
          <div className="md:hidden mb-5 flex justify-center" data-id="element-713">
            <div className="relative w-[85%]" data-id="element-714">
              <div className="absolute -inset-1 rounded-xl blur-md opacity-40" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
            }} data-id="element-715" />
              <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
              aspectRatio: '1/1'
            }} data-id="element-716">
                <img src="https://cdn.magicpatterns.com/uploads/jKCruccLSCp2Por1y9MGJ5/BTTF.4D.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" loading="eager" decoding="async" width="400" height="400" data-id="element-717" />
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 justify-center md:justify-start mb-5" data-id="element-718">
            <div className="flex gap-0.5" data-id="element-719">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-720" />)}
            </div>
            <span className="text-white font-bold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-721">
              4.9
            </span>
            <span className="text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-722">
              (5,354+ Verified Reviews)
            </span>
          </div>

          {/* Audio Player */}
          <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-orange-500/30 px-4 py-3 rounded-lg backdrop-blur-sm shadow-[0_0_15px_rgba(255,108,0,0.15)] mb-6" data-id="element-723">
            <div className="flex items-center gap-2 mb-2" data-id="element-724">
              <span className="text-base" data-id="element-725">🎧</span>
              <span className="text-sm font-semibold text-orange-200" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-726">
                Press Play: Hear How It All Began (1 min preview).
              </span>
            </div>
            <AudioPlayer src="https://cdn.shopify.com/s/files/1/0586/4069/5378/files/BIT_Audiobook_Preview.mp3?v=1762440014" data-id="element-727" />
            <p className="text-xs text-gray-400 mt-2 text-center" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-728">
              Hear the ticking clocks that started everything.
            </p>
          </div>
        </div>

        {/* Right: desktop image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-729">
          <div className="relative w-full max-w-md" data-id="element-730">
            <div className="absolute -inset-2 rounded-2xl blur-xl opacity-30" style={{
            background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
          }} data-id="element-731" />
            <div className="relative rounded-2xl overflow-hidden border border-orange-500/30 shadow-[0_0_60px_rgba(255,108,0,0.2)]" style={{
            aspectRatio: '1/1'
          }} data-id="element-732">
              <img src="https://cdn.magicpatterns.com/uploads/jKCruccLSCp2Por1y9MGJ5/BTTF.4D.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" loading="eager" decoding="async" width="600" height="600" data-id="element-733" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── IDENTIFICATION BLOCK ─────────────────────────────────────────────────
const IdentificationInt02 = () => <section className="py-4 md:py-6" style={{
  backgroundColor: BG
}} data-id="element-734">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-735">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-5 font-future tracking-wide" data-id="element-736">
        This Is For You If...
      </h2>

      <div className="space-y-3 mb-5" data-id="element-737">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-738">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-739">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-740">
            You still get chills when the lightning hits the clock tower, and
            you've never been able to fully explain why
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-741">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-742">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-743">
            You've rewatched it more times than you can count, and it still hits
            differently than anything made in the last 40 years
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-744">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-745">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-746">
            You've tried to put into words what made this film different, and
            realized no words were ever quite enough
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-747">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-748">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-749">
            You feel like something that genuinely shaped who you are deserves
            more than a Wikipedia page and a streaming algorithm
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-750">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-751">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-752">
            You want to finally understand, not just feel, why this story
            refuses to leave you alone
          </p>
        </div>
      </div>

      <p className="text-white/60 text-sm md:text-base leading-relaxed text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-753">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film, and is ready to finally
        understand it.
      </p>
    </div>
  </section>;
// ─── WHAT YOU GET STACK ────────────────────────────────────────────────────
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BookImage = ({
  src,
  alt
}: {
  src: string;
  alt: string;
}) => <div className="w-full" data-id="element-754">
    <div className="relative mx-auto max-w-[75%]" data-id="element-755">
      <img src={src} alt={alt} className="rounded-lg w-full object-cover shadow-lg" style={{
      aspectRatio: '3/4'
    }} width="180" height="240" loading="lazy" decoding="async" data-id="element-756" />
    </div>
  </div>;
const WhatYouGetInt02 = () => {
  return <section className="py-4 md:py-6" style={{
    backgroundColor: BG
  }} data-id="element-757">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-758">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-759">
          What Makes This Different
        </h2>
        <p className="text-center text-white/50 text-sm mb-4" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-760">
          Four volumes. Every angle. Nothing left out.
        </p>

        <div className="grid grid-cols-2 gap-3 md:gap-4" data-id="element-761">
          {/* Book 1 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-762">
            <BookImage src={BOOK_IMAGES[0]} alt="Back In Time, The Cultural Analysis" data-id="element-763" />
            <div className="mt-3" data-id="element-764">
              <div className="flex items-center gap-2 mb-1.5" data-id="element-765">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-766">
                  BOOK 1
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-767">
                IT DOCUMENTS THE LEGACY — NOT THE PLOT
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-768">
                190+ pages covering the cultural architecture of a story that's
                outlasted every trend, every franchise reboot, and every piece
                of technology it predicted.
              </p>
            </div>
          </div>

          {/* Book 2 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-769">
            <BookImage src={BOOK_IMAGES[1]} alt="Behind The Clock Tower" data-id="element-770" />
            <div className="mt-3" data-id="element-771">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-772">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-773">
                  BOOK 2
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-774">
                  <span className="line-through text-white/40" data-id="element-775">USD $29.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-776">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-777">
                THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-778">
                Michael J. Fox working 18-hour days, the DeLorean that kept
                breaking down on night shoots, and the casting decision that
                nearly bankrupted the production.
              </p>
            </div>
          </div>

          {/* Book 3 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-779">
            <BookImage src={BOOK_IMAGES[2]} alt="Great Scott!" data-id="element-780" />
            <div className="mt-3" data-id="element-781">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-782">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-783">
                  BOOK 3
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-784">
                  <span className="line-through text-white/40" data-id="element-785">USD $19.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-786">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-787">
                40 VOICES. 40 YEARS. EVERY ONE COUNTS.
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-788">
                Not just the famous quotes. The layered ones that hit completely
                differently on the 20th viewing. Each one contextualized with
                real-world resonance.
              </p>
            </div>
          </div>

          {/* Book 4 */}
          <div className="flex flex-col p-3 md:p-4 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-789">
            <BookImage src={BOOK_IMAGES[3]} alt="Time. Imagination. Destiny." data-id="element-790" />
            <div className="mt-3" data-id="element-791">
              <div className="flex flex-col items-start gap-0.5 mb-1.5" data-id="element-792">
                <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-793">
                  BOOK 4
                </span>
                <span className="text-[10px] md:text-xs font-semibold" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-794">
                  <span className="line-through text-white/40" data-id="element-795">USD $39.99</span>{' '}
                  <span className="text-green-400 font-bold" data-id="element-796">
                    Included for Free
                  </span>
                </span>
              </div>
              <h3 className="text-xs md:text-sm font-bold text-white mb-1.5 font-future leading-tight" data-id="element-797">
                THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU
              </h3>
              <p className="text-white/65 text-[11px] md:text-xs leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-798">
                Why does a movie about a teenager in a time machine still feel
                this important 40 years later? This volume explores free will,
                identity, and the courage to change your future.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// ─── PRICING + CART POPUP ─────────────────────────────────────────────────────
const PurchaseInt02 = () => {
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [showAudioPopup, setShowAudioPopup] = useState(false);
  const [triviaAdded, setTriviaAdded] = useState(false);
  const [companionAdded, setCompanionAdded] = useState(false);
  const [hillValleyAdded, setHillValleyAdded] = useState(false);
  const [audiobookAdded, setAudiobookAdded] = useState(false);
  const [triviaExpanded, setTriviaExpanded] = useState(false);
  const [companionExpanded, setCompanionExpanded] = useState(false);
  const [hillValleyExpanded, setHillValleyExpanded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  const cartTotal = BASE_PRICE + (triviaAdded ? TRIVIA_PRICE : 0) + (companionAdded ? COMPANION_PRICE : 0) + (hillValleyAdded ? HILLVALLEY_PRICE : 0);
  const buildCheckoutUrl = (includeAudiobook: boolean = false) => {
    const variants = ['42231842701394:1'];
    if (triviaAdded) variants.push('42361524977746:1');
    if (companionAdded) variants.push('42361526321234:1');
    if (hillValleyAdded) variants.push('42361527107666:1');
    if (includeAudiobook) variants.push('42226406948946:1');
    let url = `https://legendaryreads.com/cart/${variants.join(',')}?checkout`;
    const clickId = new URLSearchParams(window.location.search).get('click_id');
    if (clickId) {
      url += `&click_id=${encodeURIComponent(clickId)}`;
    }
    return url;
  };
  const handleCta = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCartPopup(true);
    trackInt02('Complete Digital Collection', BASE_PRICE);
  };
  const handleCheckoutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCartPopup(false);
    setShowAudioPopup(true);
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current && !sessionStorage.getItem('pricing_section_viewed')) {
          hasTrackedRef.current = true;
          sessionStorage.setItem('pricing_section_viewed', 'true');
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '16px',
    paddingBottom: '32px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-799">
      <div className="container mx-auto px-3 md:px-10 max-w-2xl" data-id="element-800">
        <div className="text-center mb-3 md:mb-10" data-id="element-801">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-802">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-803">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-804">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)] overflow-hidden" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-805">
          <div className="p-4 md:p-10" data-id="element-806">
            <div className="flex justify-center mb-4 md:mb-6" data-id="element-807">
              <img src="https://cdn.magicpatterns.com/uploads/4vE8MupRADjWMXHGaBYxus/BTTF.4D.FAN.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-808" />
            </div>

            <div className="text-center mb-4" data-id="element-809">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2 font-future tracking-wide" data-id="element-810">
                Complete Digital Collection
              </h3>
            </div>

            <ul className="space-y-2 mb-6 max-w-sm mx-auto" data-id="element-811">
              {['Back In Time, 40th Anniversary Edition (190 pages)', 'Behind The Clock Tower, Stories From The Set (87 pages)', 'Great Scott! 40 Quotes For 40 Years (87 pages)', 'Time. Imagination. Destiny., The Philosophical Legacy (71 pages)', 'Instant Download, All 4 Books'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-812">
                  <span style={{
                color: ACCENT,
                flexShrink: 0,
                marginTop: '3px'
              }} data-id="element-813">
                    ✦
                  </span>
                  <span className="text-sm leading-snug" style={{
                color: 'rgba(245,245,245,0.85)',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-814">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className="text-center mb-2" data-id="element-815">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-816">
                USD $119.97
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-817">
                USD $59.97
              </span>
            </div>

            <div className="text-center mb-4 md:mb-6" data-id="element-818">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-819">
                You Save USD $60.00
              </span>
            </div>

            <div data-id="element-820">
              <OrangeButton onClick={handleCta} data-id="element-821">
                GET MY COLLECTION →
              </OrangeButton>
            </div>
          </div>
        </div>
      </div>

      {/* ── CART POPUP MODAL ── */}
      {showCartPopup && (() => {
      const totalSavings = 60 + (triviaAdded ? 10 : 0) + (companionAdded ? 12 : 0) + (hillValleyAdded ? 12 : 0);
      return <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/85" onClick={() => setShowCartPopup(false)} data-id="element-822">
              <div className="relative w-full md:max-w-[480px] md:rounded-xl overflow-hidden shadow-[0_0_50px_rgba(255,108,0,0.15)] flex flex-col max-h-[100dvh] md:max-h-[90vh]" style={{
          backgroundColor: '#111'
        }} onClick={e => e.stopPropagation()} data-id="element-823">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 md:px-5 md:py-4 flex-shrink-0" style={{
            borderBottom: '1px solid rgba(255,108,0,0.2)'
          }} data-id="element-824">
                  <h3 className="text-base md:text-lg font-bold font-future tracking-wide" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }} data-id="element-825">
                    Your Cart
                  </h3>
                  <button onClick={() => setShowCartPopup(false)} className="text-white/50 hover:text-white transition-colors p-1" data-id="element-826">
                    <XIcon className="h-5 w-5" data-id="element-827" />
                  </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto overscroll-contain pb-4" style={{
            WebkitOverflowScrolling: 'touch'
          }} data-id="element-828">
                  {/* Main Product - Hero Card */}
                  <div className="mx-3 md:mx-4 mt-3 md:mt-4 rounded-lg overflow-hidden" style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255,108,0,0.25)'
            }} data-id="element-829">
                    <div className="px-3 py-1.5 flex items-center justify-center gap-1.5" style={{
                background: 'linear-gradient(90deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))'
              }} data-id="element-830">
                      <CheckIcon className="h-3.5 w-3.5 text-green-400" data-id="element-831" />
                      <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-green-400" data-id="element-832">
                        Added to Cart
                      </span>
                    </div>
                    <div className="p-3 md:p-4 flex gap-3 md:gap-4 items-center" data-id="element-833">
                      <div className="w-[72px] h-[72px] md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 border border-orange-500/20" data-id="element-834">
                        <img src="https://cdn.magicpatterns.com/uploads/4vE8MupRADjWMXHGaBYxus/BTTF.4D.FAN.png" alt="Complete Digital Collection" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-835" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-836">
                        <h4 className="text-sm md:text-base font-bold text-white leading-tight mb-1" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-837">
                          COMPLETE DIGITAL COLLECTION
                        </h4>
                        <p className="text-[11px] text-white/50 mb-2" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-838">
                          4 Books · Instant Download
                        </p>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-839">
                          <span className="line-through text-xs text-white/35" data-id="element-840">
                            $119.97
                          </span>
                          <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-841">
                            -50% OFF
                          </span>
                        </div>
                        <p className="text-xl md:text-2xl font-bold text-white mt-1" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-842">
                          $59.97
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Divider with label */}
                  <div className="flex items-center gap-3 px-4 mt-4 mb-3" data-id="element-843">
                    <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-white/40" data-id="element-844">
                      Today Only
                    </span>
                    <div className="flex-1 h-px bg-white/10" data-id="element-845"></div>
                    <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT
              }} data-id="element-846">
                      Special Offer
                    </span>
                  </div>

                  {/* Order Bump 1: Fan Trivia Vault */}
                  <div className={`mx-3 md:mx-4 mb-3 rounded-lg transition-all duration-200 cursor-pointer overflow-hidden ${triviaAdded ? 'ring-2 ring-orange-500' : ''}`} style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.08)'
            }} onClick={() => setTriviaAdded(!triviaAdded)} data-id="element-847">
                    <div className="p-3 md:p-4 flex gap-3 items-start" data-id="element-848">
                      <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-lg overflow-hidden flex-shrink-0 border border-white/10" data-id="element-849">
                        <img src="https://cdn.magicpatterns.com/uploads/4X6GBWnZZUX2xiMFZHHCRF/OB1_Fan-Trivia-Vault_cover.png" alt="The Fan Trivia Vault" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-850" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-851">
                        <h4 className="text-[13px] md:text-sm font-bold text-white leading-tight mb-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-852">
                          🧠 The Fan Trivia Vault
                        </h4>
                        <p className="text-[11px] text-white/50 leading-snug mb-1" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-853">
                          100 questions, production secrets, and details so
                          specific even die-hard fans get stumped.
                        </p>
                        {triviaExpanded && <p className="text-[11px] text-white/45 leading-snug mb-1 animate-[fadeIn_0.2s_ease-out]" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-854">
                            Four levels. One question at a time. Casual Fan →
                            True Believer → Production Insider → Legacy Scholar.
                            Perfect for solo deep-dives — or for the moment your
                            friends ask: "How much do you actually know about
                            this movie?"
                          </p>}
                        <button onClick={e => {
                    e.stopPropagation();
                    setTriviaExpanded(!triviaExpanded);
                  }} className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition-colors mb-2 flex items-center gap-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-855">
                          {triviaExpanded ? 'See less' : 'See more'}
                          <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${triviaExpanded ? 'rotate-180' : ''}`} data-id="element-856" />
                        </button>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-857">
                          <span className="text-base md:text-lg font-bold text-white" style={{
                      fontFamily: 'Inter, sans-serif'
                    }} data-id="element-858">
                            $9.97
                          </span>
                          <span className="line-through text-[11px] text-white/35" data-id="element-859">
                            $19.97
                          </span>
                          <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-860">
                            -50%
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 pt-1" data-id="element-861">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${triviaAdded ? 'bg-green-500 border-green-500' : 'border-white/25'}`} data-id="element-862">
                          {triviaAdded && <CheckIcon className="h-4 w-4 text-white" data-id="element-863" />}
                        </div>
                      </div>
                    </div>
                    {triviaAdded && <div className="px-3 pb-2 flex items-center gap-1.5" data-id="element-864">
                        <CheckIcon className="h-3 w-3 text-green-400" data-id="element-865" />
                        <span className="text-[10px] font-bold text-green-400 uppercase tracking-wide" data-id="element-866">
                          Added
                        </span>
                      </div>}
                  </div>

                  {/* Order Bump 2: Collector's Companion */}
                  <div className={`mx-3 md:mx-4 mb-3 rounded-lg transition-all duration-200 cursor-pointer overflow-hidden ${companionAdded ? 'ring-2 ring-orange-500' : ''}`} style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.08)'
            }} onClick={() => setCompanionAdded(!companionAdded)} data-id="element-867">
                    <div className="p-3 md:p-4 flex gap-3 items-start" data-id="element-868">
                      <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-lg overflow-hidden flex-shrink-0 border border-white/10" data-id="element-869">
                        <img src="https://cdn.magicpatterns.com/uploads/sWHA7Xq9y8HAKxSSqSya3U/OB2_Collectors-Companion_cover.png" alt="The Collector's Companion" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-870" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-871">
                        <h4 className="text-[13px] md:text-sm font-bold text-white leading-tight mb-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-872">
                          📖 The Collector's Companion
                        </h4>
                        <p className="text-[11px] text-white/50 leading-snug mb-1" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-873">
                          Your complete guide to the collection. Reading paths,
                          cross-references, glossary, and 40 discussion
                          questions.
                        </p>
                        {companionExpanded && <p className="text-[11px] text-white/45 leading-snug mb-1 animate-[fadeIn_0.2s_ease-out]" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-874">
                            You just bought four volumes. Nearly 500 pages. This
                            is the map. Cross-references between books that
                            unlock insights no single volume achieves alone.
                            Without this, you'll read the collection. With this,
                            you'll study it.
                          </p>}
                        <button onClick={e => {
                    e.stopPropagation();
                    setCompanionExpanded(!companionExpanded);
                  }} className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition-colors mb-2 flex items-center gap-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-875">
                          {companionExpanded ? 'See less' : 'See more'}
                          <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${companionExpanded ? 'rotate-180' : ''}`} data-id="element-876" />
                        </button>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-877">
                          <span className="text-base md:text-lg font-bold text-white" style={{
                      fontFamily: 'Inter, sans-serif'
                    }} data-id="element-878">
                            $17.97
                          </span>
                          <span className="line-through text-[11px] text-white/35" data-id="element-879">
                            $29.97
                          </span>
                          <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-880">
                            -40%
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 pt-1" data-id="element-881">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${companionAdded ? 'bg-green-500 border-green-500' : 'border-white/25'}`} data-id="element-882">
                          {companionAdded && <CheckIcon className="h-4 w-4 text-white" data-id="element-883" />}
                        </div>
                      </div>
                    </div>
                    {companionAdded && <div className="px-3 pb-2 flex items-center gap-1.5" data-id="element-884">
                        <CheckIcon className="h-3 w-3 text-green-400" data-id="element-885" />
                        <span className="text-[10px] font-bold text-green-400 uppercase tracking-wide" data-id="element-886">
                          Added
                        </span>
                      </div>}
                  </div>

                  {/* Order Bump 3: Hill Valley, USA */}
                  <div className={`mx-3 md:mx-4 mb-3 rounded-lg transition-all duration-200 cursor-pointer overflow-hidden ${hillValleyAdded ? 'ring-2 ring-orange-500' : ''}`} style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.08)'
            }} onClick={() => setHillValleyAdded(!hillValleyAdded)} data-id="element-887">
                    <div className="p-3 md:p-4 flex gap-3 items-start" data-id="element-888">
                      <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-lg overflow-hidden flex-shrink-0 border border-white/10" data-id="element-889">
                        <img src="https://cdn.magicpatterns.com/uploads/a8kFoEH6L3USV6GCjPzLbh/OB3_Hill-Valley-USA_cover.png" alt="Hill Valley, USA" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-890" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-891">
                        <h4 className="text-[13px] md:text-sm font-bold text-white leading-tight mb-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-892">
                          📍 Hill Valley, USA
                        </h4>
                        <p className="text-[11px] text-white/50 leading-snug mb-1" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-893">
                          13 real filming locations. GPS coordinates. Current
                          status in 2025. The guide to see them before they're
                          gone.
                        </p>
                        {hillValleyExpanded && <p className="text-[11px] text-white/45 leading-snug mb-1 animate-[fadeIn_0.2s_ease-out]" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-894">
                            Exact addresses. What to photograph. How to visit —
                            and which ones are disappearing. The Twin Pines Mall
                            parking lot has been sold for demolition. This is
                            the guide to see it before it's gone.
                          </p>}
                        <button onClick={e => {
                    e.stopPropagation();
                    setHillValleyExpanded(!hillValleyExpanded);
                  }} className="text-[10px] font-semibold text-orange-400 hover:text-orange-300 transition-colors mb-2 flex items-center gap-0.5" style={{
                    fontFamily: 'Inter, sans-serif'
                  }} data-id="element-895">
                          {hillValleyExpanded ? 'See less' : 'See more'}
                          <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${hillValleyExpanded ? 'rotate-180' : ''}`} data-id="element-896" />
                        </button>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-897">
                          <span className="text-base md:text-lg font-bold text-white" style={{
                      fontFamily: 'Inter, sans-serif'
                    }} data-id="element-898">
                            $12.97
                          </span>
                          <span className="line-through text-[11px] text-white/35" data-id="element-899">
                            $24.97
                          </span>
                          <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-900">
                            -48%
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 pt-1" data-id="element-901">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${hillValleyAdded ? 'bg-green-500 border-green-500' : 'border-white/25'}`} data-id="element-902">
                          {hillValleyAdded && <CheckIcon className="h-4 w-4 text-white" data-id="element-903" />}
                        </div>
                      </div>
                    </div>
                    {hillValleyAdded && <div className="px-3 pb-2 flex items-center gap-1.5" data-id="element-904">
                        <CheckIcon className="h-3 w-3 text-green-400" data-id="element-905" />
                        <span className="text-[10px] font-bold text-green-400 uppercase tracking-wide" data-id="element-906">
                          Added
                        </span>
                      </div>}
                  </div>
                </div>

                {/* Sticky Footer */}
                <div className="flex-shrink-0 border-t px-4 py-3 md:px-5 md:py-4" style={{
            backgroundColor: '#0d0d0d',
            borderColor: 'rgba(255,108,0,0.2)'
          }} data-id="element-907">
                  <div className="flex justify-between items-center mb-1" data-id="element-908">
                    <span className="text-sm text-white/60 font-medium" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-909">
                      Total
                    </span>
                    <span className="text-xl md:text-2xl font-bold text-white tracking-tight" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-910">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-3" data-id="element-911">
                    <span className="text-xs text-white/40" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-912">
                      You save
                    </span>
                    <span className="text-sm font-bold text-green-400" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-913">
                      -${totalSavings.toFixed(2)}
                    </span>
                  </div>
                  <OrangeButton onClick={handleCheckoutClick} data-id="element-914">
                    COMPLETE MY ORDER →
                  </OrangeButton>
                </div>
              </div>
            </div>;
    })()}

      {/* ── SECOND POPUP: AUDIO EXPERIENCE UPSELL ── */}
      {showAudioPopup && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-0 md:p-4" onClick={() => {
      setShowAudioPopup(false);
      window.location.href = buildCheckoutUrl(false);
    }} data-id="element-915">
          <div className="relative w-full max-w-[520px] h-full md:h-auto md:max-h-[90vh] md:rounded-2xl overflow-hidden shadow-[0_0_80px_rgba(255,108,0,0.4)] flex flex-col md:overflow-y-auto" style={{
        backgroundColor: '#080808',
        border: '1px solid rgba(255,108,0,0.3)'
      }} onClick={e => e.stopPropagation()} data-id="element-916">
            {/* High Impact Hero Image - Showing full image */}
            <div className="relative w-full aspect-square md:aspect-[16/10] overflow-hidden bg-black flex-shrink-0" data-id="element-917">
              <img src="https://cdn.magicpatterns.com/uploads/oarayNDUxWAzz4m4VQJgdu/POPUP_Audiobook-Experience.png" alt="The Back In Time Audiobook Experience" className="w-full h-full object-contain scale-100 animate-pulse-slow" loading="eager" decoding="async" fetchpriority="high" data-id="element-918" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-transparent opacity-60" data-id="element-919" />

              {/* Floating Badge - More prominent */}
              <div className="absolute top-6 left-6" data-id="element-920">
                <span className="bg-orange-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-[0_0_20px_rgba(234,88,12,0.5)] border border-orange-400/40 tracking-[0.15em] uppercase" data-id="element-921">
                  Special Anniversary Offer
                </span>
              </div>

              {/* Content Overlay on Image */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-center" data-id="element-922">
                <h3 className="text-2xl md:text-3xl font-bold font-future text-white tracking-wide mb-2 drop-shadow-lg" data-id="element-923">
                  WAIT! DON'T MISS THIS...
                </h3>
                <p className="text-base md:text-lg text-orange-400 font-bold tracking-tight drop-shadow-md" data-id="element-924">
                  Add the Audiobook Experience for 42% OFF
                </p>
              </div>
            </div>

            <div className="px-6 py-4 md:px-8 md:py-6 flex-1 overflow-y-auto" data-id="element-925">
              <div className="space-y-6" data-id="element-926">
                <div className="flex items-center justify-center gap-4 py-3 border-y border-white/10" data-id="element-927">
                  <span className="line-through text-base text-white/30 font-medium" data-id="element-928">
                    $42.97
                  </span>
                  <span className="text-4xl font-bold bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text drop-shadow-sm" data-id="element-929">
                    $24.97
                  </span>
                  <span className="bg-green-500/20 text-green-400 text-[11px] font-black px-2.5 py-1 rounded border border-green-500/30" data-id="element-930">
                    SAVE $18.00
                  </span>
                </div>

                <div className="space-y-4" data-id="element-931">
                  <p className="text-white/80 text-sm md:text-base leading-relaxed text-center italic font-medium" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-932">
                    "The ultimate companion for your journey. 5 hours of
                    immersive narration, production secrets, and the deep legacy
                    of Back to the Future. Take the story with you on your
                    commute, at the gym, or anywhere you want to dive deeper
                    into the 88 MPH magic."
                  </p>

                  <div className="flex flex-col gap-2 items-center text-[11px] md:text-xs text-white/40 font-bold uppercase tracking-widest" data-id="element-933">
                    <div className="flex items-center gap-2" data-id="element-934">
                      <CheckIcon className="h-3 w-3 text-orange-500" data-id="element-935" />
                      <span data-id="element-936">Instant Digital Access</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-937">
                      <CheckIcon className="h-3 w-3 text-orange-500" data-id="element-938" />
                      <span data-id="element-939">High-Quality Studio Audio</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer / Buttons - More impactful CTA */}
            <div className="p-6 md:p-8 bg-[#050505] border-t border-white/5 space-y-4" data-id="element-940">
              <button onClick={() => {
            window.location.href = buildCheckoutUrl(true);
          }} className="w-full py-5 px-6 rounded-xl font-bold text-lg tracking-wide transition-all duration-300 transform active:scale-[0.98] shadow-[0_0_30px_rgba(255,108,0,0.3)] hover:shadow-[0_0_40px_rgba(255,108,0,0.5)] hover:brightness-110 flex items-center justify-center gap-3" style={{
            background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
            color: '#0B0B0B',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-941">
                🎧 YES! ADD TO MY ORDER & CHECKOUT
              </button>
              <button onClick={() => {
            window.location.href = buildCheckoutUrl(false);
          }} className="w-full text-center text-sm font-semibold text-white/30 hover:text-white/60 transition-colors py-2 cursor-pointer uppercase tracking-widest" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-942">
                No thanks — Just checkout →
              </button>
            </div>
          </div>
        </div>}
    </section>;
};
// ─── LANDING INT02 EXPORT ────────────────────────────────────────────────────────
export function LandingInt02() {
  return <div className="w-full min-h-screen" style={{
    backgroundColor: BG,
    color: '#F5F5F5'
  }} data-id="element-943">
      <main data-id="element-944">
        <HeroInt02 data-id="element-945" />
        <IdentificationInt02 data-id="element-946" />
        <WhatYouGetInt02 data-id="element-947" />
        <Suspense fallback={<div className="h-64 bg-[#0B0B0B]" data-id="element-949" />} data-id="element-948">
          <TestimonialsInt02 data-id="element-950" />
        </Suspense>
        <PurchaseInt02 data-id="element-951" />
        <Suspense fallback={<div className="h-64 bg-[#0B0B0B]" data-id="element-953" />} data-id="element-952">
          <FAQInt02 data-id="element-954" />
        </Suspense>
      </main>
      <Footer data-id="element-955" />
      <Suspense fallback={null} data-id="element-956">
        <SocialProofNotificationInt02 data-id="element-957" />
      </Suspense>
    </div>;
}